import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function ReputationManagementPage() {
  const features = [
    'Review Monitoring & Alerts',
    'Review Generation Campaigns',
    'Response Management',
    'Multi-Platform Coverage',
    'Negative Review Mitigation',
    'Rating Improvement Strategy',
    'Patient Feedback Surveys',
    'Reputation Reporting',
    'Google Business Optimization',
    'Social Proof Integration',
    'Competitor Review Analysis',
    'Review Widget for Website'
  ];

  const platforms = [
    { name: 'Google Business Profile', icon: 'ri-google-fill' },
    { name: 'Healthgrades', icon: 'ri-heart-pulse-line' },
    { name: 'Vitals', icon: 'ri-stethoscope-line' },
    { name: 'RateMDs', icon: 'ri-user-star-line' },
    { name: 'Yelp', icon: 'ri-star-line' },
    { name: 'Facebook', icon: 'ri-facebook-fill' },
    { name: 'Zocdoc', icon: 'ri-calendar-check-line' },
    { name: 'WebMD', icon: 'ri-medicine-bottle-line' }
  ];

  const benefits = [
    {
      icon: 'ri-shield-star-line',
      title: 'Build Patient Trust',
      description: 'Positive reviews are the #1 factor patients consider when choosing a healthcare provider. Build trust at scale.'
    },
    {
      icon: 'ri-search-line',
      title: 'Improve Local SEO',
      description: 'More reviews with higher ratings directly improve your local search rankings and visibility.'
    },
    {
      icon: 'ri-user-add-line',
      title: 'Attract More Patients',
      description: 'Practices with 4.5+ star ratings and 50+ reviews convert significantly more website visitors into patients.'
    },
    {
      icon: 'ri-chat-smile-3-line',
      title: 'Professional Responses',
      description: 'Every review gets a thoughtful, HIPAA-compliant response that shows you care about patient feedback.'
    },
    {
      icon: 'ri-alert-line',
      title: 'Damage Control',
      description: 'Quickly address negative reviews with strategic responses that minimize damage and show accountability.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Continuous Improvement',
      description: 'Patient feedback reveals opportunities to improve your practice operations and patient experience.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Reputation Audit',
      description: 'Analyze your current reviews across all platforms, identify strengths, weaknesses, and opportunities.'
    },
    {
      step: '02',
      title: 'Review Generation Setup',
      description: 'Implement automated systems to request reviews from satisfied patients via email and SMS.'
    },
    {
      step: '03',
      title: 'Response Strategy',
      description: 'Develop HIPAA-compliant response templates and protocols for positive, neutral, and negative reviews.'
    },
    {
      step: '04',
      title: 'Monitoring & Alerts',
      description: 'Set up real-time monitoring and alerts so you never miss a new review on any platform.'
    },
    {
      step: '05',
      title: 'Ongoing Management',
      description: 'Continuously generate new reviews, respond to all feedback, and improve your overall rating.'
    }
  ];

  const stats = [
    { number: '84%', label: 'of patients trust online reviews as much as personal recommendations' },
    { number: '4.0+', label: 'star rating is the minimum patients expect from healthcare providers' },
    { number: '94%', label: 'of patients say positive reviews make them more likely to choose a provider' },
    { number: '3x', label: 'more likely to be chosen with 50+ reviews vs. competitors with fewer' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                REPUTATION MANAGEMENT
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Build Trust &amp; Authority with Strategic Review Management
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Generate more positive reviews, respond professionally to all feedback, and build the online reputation that makes patients choose you over competitors.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Reputation Audit</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/4ecd814a1c1dabda12329296552b6a54.jpg"
                  alt="Healthcare reputation management"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 lg:px-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#7C3AED] mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY REPUTATION MATTERS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Your Online Reputation Directly Impacts Patient Growth
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Patients research providers online before booking. A strong reputation with abundant positive reviews is your most powerful marketing asset.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              PLATFORMS WE MONITOR
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Complete Coverage Across All Major Review Sites
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We monitor and manage your reputation across every platform where patients leave healthcare reviews.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100 text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${platform.icon} text-xl text-white`}></i>
                </div>
                <div className="text-sm font-bold text-gray-900">
                  {platform.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Reputation%20management%20dashboard%20showing%20review%20monitoring%20alerts%20response%20management%20and%20rating%20improvement%20analytics%20for%20healthcare%20practice%20professional%20interface%20with%20patient%20feedback%20and%20review%20generation%20tools%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=repfeatures1&orientation=portrait"
                  alt="Reputation management features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE SERVICES
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Complete Reputation Management Solution
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From automated review generation to professional response management, we handle every aspect of your online reputation.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Strategic Reputation Building That Works
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our systematic approach generates consistent positive reviews while professionally managing all patient feedback.
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-white">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Build a 5-Star Reputation?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a free reputation audit showing your current ratings, review volume, and a custom strategy to improve your online reputation.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Free Reputation Audit</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Success Stories</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="reputation-management" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
